/* descriptiveStatisticsThunk.ts */

/* Redux-Toolkit */
import { createAsyncThunk } from "@reduxjs/toolkit";

/* Axios */
import axios, { HttpStatusCode } from "axios";

/* Utils */
import axiosInterceptor from "src/utils/helper/axios-interceptor";
import { InsightsFilterRequestDto } from "src/utils/dto/insights-filter/InsightsFilterRequestDto";
import { BaseChartResponseDto } from "src/utils/dto/charts/BaseChartResponseDto";
import { transformDescriptiveStatisticsData } from "src/utils/helper/descriptive-statistics/transformDescriptiveStatisticsData";
import { ClientError } from "src/utils/status-handling/ClientError";

/* Constants */
import { ErrorMessage } from "src/shared/constants/common/ErrorMessage";

/* Interfaces */
import { BaseChartProps } from "src/shared/interfaces/charts/BaseChart";
import { DescriptiveStatisticsProps } from "src/shared/interfaces/DescriptiveStatistics";
import { BaseInsightsFilterRequest } from "src/shared/interfaces/api/InsightsFilters";
import { ChartType } from "src/shared/constants/charts/Chart";

export const fetchDescriptiveStatistics = createAsyncThunk<
  BaseChartProps<DescriptiveStatisticsProps, ChartType[]>,
  BaseInsightsFilterRequest,
  {
    rejectValue: ClientError;
  }
>(
  "descriptiveStatistics/fetchDescriptiveStatistics",
  async (
    params: BaseInsightsFilterRequest,
    { rejectWithValue }
  ) => {
    try {
      const requestParams =
        InsightsFilterRequestDto.toBaseInsightsFilterRequest(
          params
        );

      const response = await axiosInterceptor.get(
        "/data/cohort_data_statistics",
        {
          params: {
            ...requestParams,
            // Never request completeness states for a custom cohort - it has no
            // valid denominator (mirrors the display-side custom-cohort gate).
            include_no_value_states: !requestParams.custom_cohorts_str,
          },
          paramsSerializer: { indexes: null },
        }
      );

      if (response.status === HttpStatusCode.NoContent) {
        return rejectWithValue(ClientError.noContent());
      }

      const dto = BaseChartResponseDto.fromApi(
        response.data,
        transformDescriptiveStatisticsData
      );

      const result = dto.toObject();
      return result;
    } catch (error) {
      console.error(error);
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.Forbidden
      ) {
        return rejectWithValue(ClientError.forbidden(ErrorMessage.NO_CARE_SITE_ACCESS));
      }
      return rejectWithValue(ClientError.internal());
    }
  }
);
